import { CommunityAssociation, type Association } from "@entities";
import { Box } from "@mui/material";
import { useCreateHyperTable } from "@shared/frontend/hyper-table";
import { type FC, useCallback, useRef, useState } from "react";
import { UserCardPopup } from "../../components/popups/user-card/UserCardPopup";
import {
  buildCommunityMemberColumns,
  communityMemberActivityCells,
} from "./community-members-columns";
import { COMMUNITY_MEMBER_FILTERS } from "./community-members-filters";
import {
  fetchCommunityMemberActivity,
  type CommunityMemberActivity,
} from "./communityDashboardApi";

interface CommunityMembersTableProps {
  communityId: string;
  organizationId: string;
}

const CommunityMembersTable: FC<CommunityMembersTableProps> = ({
  communityId,
  organizationId,
}) => {
  const { HyperTable } = useCreateHyperTable(CommunityAssociation);
  const [activityByAssociationId, setActivityByAssociationId] = useState<
    Record<string, CommunityMemberActivity>
  >({});
  const [openAssociation, setOpenAssociation] = useState<Association>();
  const requestIdRef = useRef(0);

  const onDisplayedDataChange = useCallback(
    async (rows: CommunityAssociation[]) => {
      const associationIds = rows
        .map((row) => row.associationId)
        .filter((id): id is string => !!id);
      const requestId = ++requestIdRef.current;
      if (associationIds.length === 0) {
        setActivityByAssociationId({});
        return;
      }
      try {
        const activity = await fetchCommunityMemberActivity({
          communityId,
          organizationId,
          associationIds,
        });
        if (requestId !== requestIdRef.current) return;
        setActivityByAssociationId(
          Object.fromEntries(activity.map((row) => [row.associationId, row])),
        );
      } catch {
        if (requestId !== requestIdRef.current) return;
        setActivityByAssociationId(
          Object.fromEntries(
            associationIds.map((associationId) => [
              associationId,
              {
                associationId,
                approvedParticipationsCount: 0,
                lastParticipationAt: null,
                reportedHours: 0,
              },
            ]),
          ),
        );
      }
    },
    [communityId, organizationId],
  );

  return (
    <Box sx={{ width: "100%", height: "100%", minHeight: 0 }}>
      <HyperTable
        tableId={`community-members-${communityId}`}
        title="חברי הקהילה"
        findConfig={{
          where: CommunityAssociation.hyperFilter({
            communityId,
            association: {
              $is: {
                organizationId,
                user: { $is: { isDeleted: false } },
              },
            },
          }),
          include: {
            association: { include: { user: true } },
          },
          initialOrderBy: { createdAt: "desc" },
        }}
        onDisplayedDataChange={onDisplayedDataChange}
        onRowClick={(_, row) => {
          if (row.original.association) {
            setOpenAssociation(row.original.association);
          }
        }}
      >
        <HyperTable.Toolbar>
          {COMMUNITY_MEMBER_FILTERS.map((filter) => (
            <HyperTable.CustomFilter key={filter.id} {...(filter as any)} />
          ))}
        </HyperTable.Toolbar>
        {buildCommunityMemberColumns().map((column) => (
          <HyperTable.Column key={String(column.field)} {...column} />
        ))}
        <HyperTable.NonEntityColumn
          id="approvedParticipations"
          caption="התנדבויות"
          enableSorting={false}
          minSize={110}
          deps={[activityByAssociationId]}
          cell={({ row }) =>
            communityMemberActivityCells(activityByAssociationId).participations(
              row.original,
            )
          }
        />
        <HyperTable.NonEntityColumn
          id="lastParticipationAt"
          caption="התנדבות אחרונה"
          enableSorting={false}
          minSize={140}
          deps={[activityByAssociationId]}
          cell={({ row }) =>
            communityMemberActivityCells(activityByAssociationId).lastAt(
              row.original,
            )
          }
        />
        <HyperTable.NonEntityColumn
          id="reportedHours"
          caption="שעות מדווחות"
          enableSorting={false}
          minSize={120}
          deps={[activityByAssociationId]}
          cell={({ row }) =>
            communityMemberActivityCells(activityByAssociationId).hours(
              row.original,
            )
          }
        />
      </HyperTable>
      <UserCardPopup
        userData={openAssociation}
        setUserData={setOpenAssociation}
        handleSave={() => HyperTable.context.current?.refetch("remove")}
      />
    </Box>
  );
};

export default CommunityMembersTable;
